"use client";

import Autoplay from "embla-carousel-autoplay";
import Fade from "embla-carousel-fade";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import { useEffect, useState } from "react";

interface HeroImage {
  id: string;
  url: string;
  alt?: string | null;
  focalPoint?: {
    x: number;
    y: number;
  } | null;
  author?: string | null;
  copyright?: string | null;
}

interface HeroGalleryProps {
  images: HeroImage[];
  autoplayDelay?: number;
}

export function HeroGallery({ images, autoplayDelay = 2000 }: HeroGalleryProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      duration: 80, // Smooth transition duration
    },
    [Autoplay({ delay: autoplayDelay, stopOnInteraction: false }), Fade()],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  if (!images || images.length === 0) return null;



  return (
    <div className="relative z-0 h-screen min-h-screen w-screen lg:absolute lg:inset-0">
      <div className="embla h-full w-full" ref={emblaRef}>
        <div className="embla__container h-full">
          {images.map((image, index) => (
            <div key={image.id} className="embla__slide relative h-full flex-[0_0_100%]">
              <Image
                src={`${image.url}?w=500&h=1000&fit=crop&crop=focalpoint&fp-x=${image.focalPoint?.x || 0.5}&fp-y=${image.focalPoint?.y || 0.5}&auto=format`}
                alt={image.alt || "Hero image"}
                fill
                className="mobileimage block object-cover object-center lg:hidden"
                priority={index === 0}
                sizes="100vw"
              />
              <Image
                src={`${image.url}?w=1920&h=1080&fit=crop&crop=focalpoint&fp-x=${image.focalPoint?.x || 0.5}&fp-y=${image.focalPoint?.y || 0.5}&auto=format`}
                alt={image.alt || "Hero image"}
                fill
                className="desktopimage hidden object-cover object-center lg:block"
                priority={index === 0}
                sizes="100vw"
              />
              <div className="absolute bottom-[220px] lg:bottom-[350px] right-0 -mr-[180px] lg:-mr-[160px] w-[400px] rotate-270 origin-top text-left " >
                <p className="text-white text-[7px] text-shadow-xs opacity-80  whitespace-nowrap">
                  {image.copyright || image.author ? "Credit: " : ""}{image.copyright} {image.author}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 h-[292px] w-full z-10 hidden lg:block"
        style={{
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 4.51%, rgba(0, 0, 0, 0.50) 73.1%)',
          backgroundBlendMode: 'multiply'
        }}
      />


      {/* Gallery indicators */}
      {/* {images.length > 1 && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${index === selectedIndex
                  ? "bg-white"
                  : "bg-white/40 hover:bg-white/60"
                  }`}
                onClick={() => emblaApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      )} */}
    </div>
  );
}
